export const INCENTIVE_SECTIONS = [
    "overview",
    "campaigns",
    "review",
    "ledger",
];

export const normalizeSection = (value) =>
    INCENTIVE_SECTIONS.includes(value) ? value : "overview";

export const asRows = (value) =>
    Array.isArray(value) ? value : value?.results || value?.items || [];

export const formatNumber = (value, locale = "fa-IR") =>
    new Intl.NumberFormat(locale).format(Number(value) || 0);

export const opportunityAmount = (opportunity) => {
    if (!opportunity || opportunity.active === false) return null;
    const minimum = Number(
        opportunity.amount ??
            opportunity.fixed_amount ??
            opportunity.min_amount ??
            opportunity.reward
    );
    if (!Number.isFinite(minimum) || minimum <= 0) return null;
    const maximum = Number(opportunity.max_amount ?? minimum);
    return {
        minimum,
        maximum: Number.isFinite(maximum) && maximum > 0 ? maximum : minimum,
    };
};

export const calculateTierBudget = (tiers, participantLimit) => {
    const maximum = Math.max(
        0,
        ...(tiers || []).map((tier) => Number(tier.amount) || 0)
    );
    return maximum * Math.max(0, Number(participantLimit) || 0);
};

export const SERVICE_SCOPED_ACTIONS = [
    "feedback_submit",
    "offer",
    "introduction",
    "share",
];

export const ACTION_LABEL_KEYS = {
    feedback_submit: "feedback",
    survey_submit: "survey",
    offer: "offer",
    introduction: "introduction",
    share: "share",
    custom: "custom",
};

export const GROUP_ALL = "__all__";
export const ITEM_TYPE_ALL = "__all__";

export const isAllItemTypes = (value) => value === ITEM_TYPE_ALL;

export const ACTION_FROM_CODE = {
    "feedback.completed": "feedback_submit",
    "survey.completed": "survey_submit",
    "content_share.qualified": "share",
    "offer.created": "offer",
    "referral.qualified": "referral",
    "item_introduction.qualified": "introduction",
};

export const displayStatus = (value) => {
    const status = String(value || "draft").toLowerCase();
    if (status === "published") return "active";
    if (status === "archived") return "completed";
    return status;
};

export const toDatetimeLocal = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
        const pad = (n) => String(n).padStart(2, "0");
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
            date.getDate()
        )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }
    const match = String(value)
        .replace(" ", "T")
        .match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
    return match ? match[1] : "";
};

export const campaignToDraft = (campaign = {}) => {
    const rule = campaign.rule || {};
    const actionCode = rule.action_code || "";
    const action =
        rule.action ||
        ACTION_FROM_CODE[actionCode] ||
        (actionCode.startsWith("custom.") ? "custom" : "feedback_submit");
    const tiers = (rule.tiers || []).map((tier, index) => ({
        threshold: Number(tier.threshold || tier.position || index + 1),
        amount: Number(tier.amount ?? tier.reward ?? 0),
    }));
    const groupId = rule.user_group;
    return {
        name: campaign.name || "",
        action,
        customAction:
            action === "custom"
                ? rule.custom_action || rule.action_name || ""
                : "",
        itemTypeId:
            rule.item_type_id ||
            (SERVICE_SCOPED_ACTIONS.includes(action) &&
            (rule.target_type === "all" ||
                (!rule.item_type_id && !rule.item_id && !rule.form_id))
                ? ITEM_TYPE_ALL
                : null),
        itemId: rule.item_id || null,
        formId: rule.form_id || null,
        surveyId: rule.survey_id || null,
        audience: groupId ? "group" : "all",
        groupIds: groupId ? [groupId] : [],
        participantLimit: campaign.participant_limit ?? 100,
        perUserLimit: rule.per_user_limit ?? 1,
        verification:
            rule.evaluation_mode === "manual" ? "manual" : "automatic",
        webhookUrl: "",
        tiers: tiers.length ? tiers : [{ threshold: 1, amount: 10 }],
        budget: campaign.budget ?? campaign.total_budget ?? 1000,
        startsAt: toDatetimeLocal(campaign.starts_at),
        endsAt: toDatetimeLocal(campaign.ends_at),
    };
};

export const campaignSchedulePayload = (draft) => ({
    name: draft.name.trim(),
    starts_at: draft.startsAt || null,
    ends_at: draft.endsAt || null,
});

export const isRuleLocked = (campaign) =>
    Boolean(campaign && campaign.status && campaign.status !== "draft");

export const selectedGroupIds = (draft) => {
    if (Array.isArray(draft.groupIds) && draft.groupIds.length) {
        return draft.groupIds;
    }
    if (draft.groupId) return [draft.groupId];
    return [];
};

export const resolveCampaignTarget = (draft) => {
    if (draft.action === "survey_submit" && draft.surveyId) {
        return { targetType: "survey", targetId: draft.surveyId };
    }
    if (SERVICE_SCOPED_ACTIONS.includes(draft.action)) {
        if (isAllItemTypes(draft.itemTypeId)) {
            return { targetType: "all", targetId: null };
        }
        if (draft.formId) return { targetType: "form", targetId: draft.formId };
        if (draft.itemId) return { targetType: "item", targetId: draft.itemId };
        if (draft.itemTypeId) {
            return { targetType: "item_type", targetId: draft.itemTypeId };
        }
    }
    if (draft.targetType && draft.targetType !== "all") {
        return {
            targetType: draft.targetType,
            targetId: draft.targetId || null,
        };
    }
    return { targetType: "all", targetId: null };
};

export const draftIssues = (
    draft,
    { ruleLocked = false, isEdit = false } = {}
) => {
    const issues = [];
    if (!String(draft.name || "").trim()) {
        issues.push({ field: "name", step: 0, key: "incentives.validation.name" });
    }
    if (draft.endsAt && draft.startsAt && draft.endsAt <= draft.startsAt) {
        issues.push({
            field: "schedule",
            step: 2,
            key: "incentives.validation.schedule",
        });
    }
    if (ruleLocked) return issues;
    if (draft.action === "custom" && !String(draft.customAction || "").trim()) {
        issues.push({
            field: "customAction",
            step: 0,
            key: "incentives.validation.customAction",
        });
    }
    if (!isEdit) {
        if (SERVICE_SCOPED_ACTIONS.includes(draft.action) && !draft.itemTypeId) {
            issues.push({
                field: "itemType",
                step: 0,
                key: "incentives.validation.itemType",
            });
        }
        if (draft.action === "survey_submit" && !draft.surveyId) {
            issues.push({
                field: "survey",
                step: 0,
                key: "incentives.validation.survey",
            });
        }
    }
    if (draft.audience === "group" && !selectedGroupIds(draft).length) {
        issues.push({ field: "group", step: 1, key: "incentives.validation.group" });
    }
    if (Number(draft.participantLimit) < 1) {
        issues.push({
            field: "participants",
            step: 1,
            key: "incentives.validation.participants",
        });
    }
    if (Number(draft.perUserLimit) < 1) {
        issues.push({
            field: "perUserLimit",
            step: 1,
            key: "incentives.validation.perUser",
        });
    }
    if (
        draft.verification === "webhook" &&
        !String(draft.webhookUrl || "").trim()
    ) {
        issues.push({
            field: "webhook",
            step: 1,
            key: "incentives.validation.webhook",
        });
    }
    if ((draft.tiers || []).some((tier) => Number(tier.amount) < 1)) {
        issues.push({ field: "tier", step: 2, key: "incentives.validation.tier" });
    }
    if (Number(draft.budget) < calculateTierBudget(draft.tiers, 1)) {
        issues.push({
            field: "budget",
            step: 2,
            key: "incentives.validation.budget",
        });
    }
    return issues;
};

const firstErrorText = (value) => {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (Array.isArray(value)) {
        for (const item of value) {
            const text = firstErrorText(item);
            if (text) return text;
        }
    }
    return "";
};

const FIELD_ERROR_KEYS = {
    name: "incentives.validation.name",
    custom_action: "incentives.validation.customAction",
    action: "incentives.validation.action",
    target_id: "incentives.error.targetId",
    item_type: "incentives.validation.itemType",
    survey: "incentives.validation.survey",
    user_group: "incentives.validation.group",
    user_groups: "incentives.validation.group",
    participant_limit: "incentives.validation.participants",
    per_user_limit: "incentives.validation.perUser",
    webhook_url: "incentives.validation.webhook",
    budget: "incentives.validation.budget",
    tiers: "incentives.validation.tier",
    ends_at: "incentives.validation.schedule",
};

const CODE_ERROR_KEYS = {
    insufficient_funds: "incentives.error.insufficient_funds",
    company_required: "incentives.error.company_required",
    campaign_requires_reward_rule: "incentives.error.campaign_requires_reward_rule",
    invalid_campaign_transition: "incentives.error.invalid_campaign_transition",
    published_campaign_rules_are_immutable: "incentives.error.publishedImmutable",
    published_campaign_budget_is_immutable: "incentives.error.publishedImmutable",
    archived_campaign_is_immutable: "incentives.error.archivedImmutable",
};

export const campaignErrorMessages = (
    error,
    t,
    fallbackKey = "incentives.error.publish"
) => {
    const fallback = t(fallbackKey);
    const data = error?.response?.data;
    if (!data || typeof data !== "object") return [fallback];

    const messages = [];
    const code = typeof data.code === "string" ? data.code : "";
    if (code && CODE_ERROR_KEYS[code]) {
        messages.push(t(CODE_ERROR_KEYS[code]));
    }

    Object.entries(data).forEach(([field, value]) => {
        if (field === "code" || field === "detail" || field === "message") {
            return;
        }
        if (FIELD_ERROR_KEYS[field]) {
            messages.push(t(FIELD_ERROR_KEYS[field]));
            return;
        }
        const text = firstErrorText(value);
        if (text) messages.push(text);
    });

    if (messages.length) return [...new Set(messages)];

    const detail = firstErrorText(data.detail) || firstErrorText(data.message);
    if (detail && CODE_ERROR_KEYS[detail]) {
        return [t(CODE_ERROR_KEYS[detail])];
    }
    if (detail && detail !== code) return [detail];
    return [fallback];
};

export const campaignPayload = (draft) => {
    const { targetType, targetId } = resolveCampaignTarget(draft);
    const groupIds = selectedGroupIds(draft);
    const wantsAllGroups = groupIds.includes(GROUP_ALL);
    const concreteGroups = groupIds.filter((id) => id && id !== GROUP_ALL);
    const payload = {
        name: draft.name.trim(),
        action: draft.action,
        custom_action: draft.action === "custom" ? draft.customAction.trim() : "",
        target_type: targetType,
        target_id: targetId,
        audience: draft.audience,
        user_group:
            draft.audience === "group" &&
            !wantsAllGroups &&
            concreteGroups.length === 1
                ? concreteGroups[0]
                : null,
        participant_limit: Number(draft.participantLimit) || null,
        per_user_limit: Number(draft.perUserLimit) || 1,
        verification: draft.verification,
        webhook_url:
            draft.verification === "webhook" ? draft.webhookUrl.trim() : "",
        tiers: draft.tiers.map((tier, index) => ({
            threshold: Number(tier.threshold) || index + 1,
            amount: Number(tier.amount) || 0,
        })),
        budget: Number(draft.budget) || 0,
        starts_at: draft.startsAt || null,
        ends_at: draft.endsAt || null,
    };
    if (
        draft.audience === "group" &&
        !wantsAllGroups &&
        concreteGroups.length > 1
    ) {
        payload.user_groups = concreteGroups;
    }
    return payload;
};

export const quickSetupPayload = (value, target) => ({
    name: value.name || target?.name || "",
    action: value.action,
    target_type: target?.type,
    target_id: target?.id,
    audience: value.audience,
    user_group: value.audience === "group" ? value.groupId : null,
    tiers: [{ threshold: 1, amount: Number(value.amount) || 0 }],
    budget: Number(value.budget) || 0,
    verification: "automatic",
    status: "active",
});
